/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import SearchSelect from "@ui-components/Select/GenericSelect";
import { ReactComponent as GeolocationIcon } from "../../../../shared/assets/geolocation.svg";
import { ReactComponent as IpIcon } from "../../../../shared/assets/Icons/IconIpAddress.svg";
import { AppDispatch } from "../../../../shared/redux/store/store";
import { GenericRadioCard } from "@ui-components/RadioCard/GenericRadioCard";
import { useDispatch } from "react-redux";
import { fetchConnectionCapacity, fetchMasks, fetchPortsAndLags } from "@redux/slices/kontrolEdge/orderSlice";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import { ConnectionTermOption, formatTermLabel, transformCapacityOptions, transformConnectionTermOptions, transformMaskOptions, transformPortOptions } from "./helpers/formatData";
interface InternetLocationForm {
  portSelection: string;
  mbps: number;
  countryCode: string;
  mask: string;
  contractTerm: number;
}

export interface InternetLocationRef {
  submit: () => Promise<boolean>;
}

const InternetLocation = forwardRef<InternetLocationRef, { token: string }>(
  ({ token }, ref) => {
  // Get saved port data from Redux.
  const dispatch = useDispatch<AppDispatch>();
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const handleCheckboxChange = (checked: boolean) => {
    setCheckboxChecked(checked);
  };
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<InternetLocationForm>({
    mode: "onChange",
    defaultValues: {
      portSelection: "",
      mbps: undefined,
      countryCode: "",
      mask: "",
      contractTerm: undefined,
    },
  });

    const defaultConnectionTermOptions: ConnectionTermOption[] = [
      { label: "1 month - $ USD", value: 1 },
      { label: "12 months - $ USD", value: 12 },
      { label: "24 months - $ USD", value: 24 },
      { label: "36 months - $ USD", value: 36 },
    ];
  
  
    // Local option states.
    const [portOptions, setPortOptions] = useState<any[]>([]);
    const [capacityOptions, setCapacityOptions] = useState<any[]>([]);
    const [maskOptions, setMaskOptions] = useState<any[]>([]);
    const [connectionPricingData, setConnectionPricingData] = useState<any>({});

    // Local state for selected capacity and contract term.
    const [selectedCapacity, setSelectedCapacity] = useState<number | null>(null);
    const [selectedTerm, setSelectedTerm] = useState<number | null>(null);

    //Map of locations fetch from the enpdoint
    const countryOptions = [
      {label : 'Mexico', value: "Mexico"},
      {label : 'More countries coming soon...', value: ""}
    ];
    // Fetch data on mount and transform.
    useEffect(() => {
      if (!token) return;
      const fetchPricingData = async () => {
        try {
          // Fetch masks and transform.
          const masksRes = await dispatch(fetchMasks({ token }));
          const masksData = masksRes?.payload;
          const masksArray = transformMaskOptions(
            masksData as Record<string, any> | undefined
          );
          setMaskOptions(masksArray);

          // Fetch ports and lags and transform.
          const portsRes = await dispatch(fetchPortsAndLags({ token }));
          const portsData = portsRes?.payload;
          const portOptions = transformPortOptions(portsData);
          setPortOptions(portOptions);

          // Fetch connection capacities and transform.
          const capacitiesRes = await dispatch(fetchConnectionCapacity({ token }));
          const capacitiesData = capacitiesRes?.payload;
          setConnectionPricingData(capacitiesData);
          const capacityOptions = transformCapacityOptions(capacitiesData);
          setCapacityOptions(capacityOptions);
        } catch (error) {
          console.error("Error fetching pricing data:", error);
        }
      };
      fetchPricingData();
    }, [token, dispatch]);

    // Update capacity selection.
    const handleCapacitySelect = (selectedValue: number) => {
      setValue("mbps", selectedValue);
      setSelectedCapacity(selectedValue);
      // Reset contract term.
      setSelectedTerm(null);
      setValue("contractTerm", 0);
    };
  
    // Expose a submit method via ref.
    useImperativeHandle(ref, () => ({
      submit: async () =>
        new Promise<boolean>((resolve) => {
          handleSubmit(
            (data) => {
              resolve(true);
            },
            (errors) => {
              resolve(false);
            }
          )();
        }),
    }));
    
  const displayConnectionTermOptions: ConnectionTermOption[] =
  selectedCapacity !== null && connectionPricingData[selectedCapacity]
    ? transformConnectionTermOptions(
        connectionPricingData,
        selectedCapacity,
        formatTermLabel
      )
    : defaultConnectionTermOptions;

  
  return (
    <form>

      {/* Port Selection */}
      <div className="px-7 pt-5">
        <h3 className="text-2l font-bold mb-8 text-letf text-semantic-text-primary">Location Selection</h3>
        <Controller
            name="portSelection"
            control={control}
            rules={{ required: "You must select a port" }}
            render={({ field }) => (
            <SearchSelect
              {...field}  
              label="Select Port"
              options={portOptions}
              leftIcon={<GeolocationIcon className="w-5 h-5 text-gray-400" />}
              onSelect={(value: string) => {
                field.onChange(value);
              }}
            />
          )}
        />
        <GenericCheckbox
          label="Enable internet redundancy on two ports at the same location"
          checked={checkboxChecked}
          onChange={handleCheckboxChange}
          className="mb-5 mt-5"
        />
        {errors.portSelection && <p className="text-red-500 text-sm">{errors.portSelection.message}</p>}
        <div className="mt-5 w-80% border-b border-gray-300">
        </div>
      </div>

      {/* Connection Capacity */}
      <div className="px-7 pt-5">
        <h3 className="text-2l font-bold mb-8 text-letf text-semantic-text-primary">Select Connection Capacity</h3>
        <Controller
            name="mbps"
            control={control}
            rules={{ required: "You must select a capacity" }}
            render={({ field }) => (
            <SearchSelect
              label="Select Connection capacity"
              options={capacityOptions}
              {...field}
              leftIcon={<IpIcon className="w-5 h-5 text-gray-400" />}
              onSelect={(selectedValue) => {
                const capacityNumber =
                  typeof selectedValue === "string"
                    ? Number(selectedValue)
                    : selectedValue;
                field.onChange(capacityNumber)
                handleCapacitySelect(capacityNumber);
              }}
            />
          )}
        />
        {errors.mbps && <p className="text-red-500 text-sm">{errors.mbps.message}</p>}
        <div className="mt-5 w-80% border-b border-gray-300">
        </div>
      </div>
      
      {/* Public IPs Addressing */}
      <div className="px-7 pt-5">
          <h3 className="text-2l font-bold mb-8 text-letf text-semantic-text-primary">Public IPs Addressing</h3>
          <Controller
            name="countryCode"
            control={control}
            rules={{ required: "You must select a country" }}
            render={({ field }) => (
              <SearchSelect
                label="Select Country IP Address Required"
                options={countryOptions}
                leftIcon={<IpIcon className="w-5 h-5 text-gray-400" />}
                onSelect={(value: string) => {
                  field.onChange(value);
                }}
              />
            )}
          />
          {errors.countryCode && <p className="text-red-500 text-sm">{errors.countryCode.message}</p>}
        <div className="mt-5 w-80% ">
        </div>

        {/* Selection IP Block */}
        <Controller
            name="mask"
            control={control}
            rules={{ required: "You must select a country" }}
            render={({ field }) => (
            <SearchSelect
              label="Select IP Block Required"
              options={maskOptions}
              leftIcon={<IpIcon className="w-5 h-5 text-gray-400" />}
              onSelect={(value: string) => {
                field.onChange(value);
              }}
            />
          )}
        />
          {errors.mask && <p className="text-red-500 text-sm">{errors.mask.message}</p>}
        <div className="mt-5 w-80% border-b text-gray-400">
        </div>
      </div>

      {/* Connection Term Options */}
        <div className="px-7 pt-5">
          <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
            Select Connection Term
          </h3>
          <Controller
            name="contractTerm"
            control={control}
            rules={{
              required: "You must select a contract term",
              validate: value => value !== 0 || "Please select a valid contract term"
            }}
            
            render={({ field }) => {
              return (
              <div className="space-y-3">
                {displayConnectionTermOptions.map((option) => (
                  <GenericRadioCard
                    key={option.value}
                    name="contractTerm"
                    value={option.value}
                    label={option.label}
                    checked={field.value === option.value}
                    onChange={(e) => {
                      const numericValue = Number(e.target.value);
                      field.onChange(numericValue);
                    }}
                    className="w-[250px]"
                  />
                ))}
              </div>
              );
            }}
          />
          {errors.contractTerm && (
            <p className="text-red-500 text-sm">{errors.contractTerm.message}</p>
          )}
          <div className="mt-5 w-80% border-b border-gray-300"></div>
        </div>
    </form>
  );
});

InternetLocation.displayName = "InternetLocation";
export default InternetLocation;